<template lang="pug">
div
  v-card-title.d-flex.align-center
    span Mix Óptimo
    ShowInfoBtn(@click="$refs.infoAlert.showAlert()")
    v-spacer

    v-select.group-by-select(v-model="chartBy" :items="chartByOptions" label="Agrupar gráfico por" outlined hide-details)
    v-btn.ml-2(@click="exportData" color="primary" outlined) Exportar
    v-btn.ml-2(@click="updateMix" color="primary" outlined :loading="loadingUpdateMix") Actualizar
  v-card-text
    InfoAlert(:message="alertMessage" ref="infoAlert")

  v-card-text
    v-row
      v-col(cols="auto")
        h3.mb-2 Venta anual óptima según modelo
        h1.primary--text {{ totalSales | moneyMM }}
      v-col(cols="auto")
        h3.mb-2 Margen bruto de Mix Óptimo
        h1.primary--text {{ totalProfit | moneyMM }}

    v-row.mt-6
      v-col(cols="12")
        h3.text-center Distribución Mensual Óptima de Ventas
    v-skeleton-loader(v-if="!loaded" type="image" height="360")
    .chart-wrap(v-else style="position: relative; height: 360px; width: 100%")
      Bar(:chart-data="chartData" :chart-options="chartOptions" :chart-id="id" :plugins="chartPlugins" :styles="{position:'relative',height:'100%',width:'100%'}")
</template>


<script>
import { Bar } from 'vue-chartjs/legacy'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { mapActions } from 'vuex'
import chartsUtils from '@/utils/chartsUtils'
import InfoAlert from '../../common/InfoAlert.vue'
import ShowInfoBtn from '../../common/ShowInfoBtn.vue'
import { fmtMoneyMM } from '@/utils/customVueFilters'

ChartJS.register(Title, Tooltip, BarElement, CategoryScale, Legend, LinearScale)

export default {
  props: {
    filters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    projection: {
      type: Number,
      default: null,
    },
    totalYearProfit: {
      type: Number,
      default: 0,
    }
  },
  components: {
    Bar,
    InfoAlert,
    ShowInfoBtn,
  },
  data() {
    return {
      id: this._uid.toString(),
      chartPlugins: [ChartDataLabels],
      loadingUpdateMix: false,
      loaded: false,
      alertMessage: `
<p>
El <b>mix óptimo</b> es la combinación de ventas que te deja el <b>mayor margen posible</b>, respetando tu capacidad de producción y lo que cada cliente te compra. Concéntrate en los productos y clientes que aparecen más arriba. Puedes exportarlo y usarlo como tu plan de ventas.
</p>
`,
      marketSize: 0,
      totalProfit: 0,
      totalSales: 0,
      form: {
        growthRate: 0
      },
      chartBy: 'customer',
      chartByOptions: [
        { value: 'customer', text: 'Cliente' },
        { value: 'product', text: 'Producto' }
      ],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: true,
            ticks: { callback: (value) => fmtMoneyMM(value) }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return fmtMoneyMM(context.raw)
              },
              title: function(context) {
                const label = context[0].dataset.label
                return label
              }
            }
          },
          datalabels: {
            color: '#fff',
            font: { size: 9, weight: '500' },
            display: 'auto',
            formatter: function(value) {
              if (!value || value < 1e6) return null
              return fmtMoneyMM(value)
            }
          }
        }
      },
      chartData: {
        labels: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        datasets: [
          {
            label: 'Potencial en $',
            data: [],
            backgroundColor: chartsUtils.colors,
            hoverBackgroundColor: chartsUtils.colors
          }
        ]
      },
      v: {
        growthRate: [
          v => !!v || 'Este campo es requerido',
          v => v >= 0 || 'El valor debe ser mayor o igual a 0'
        ]
      }
    }
  },
  computed: {
    computedMarketSize() {
      return this.chartData.datasets[0].data.reduce((acc, value) => acc + value, 0)
    },
  },
  methods: {
    ...mapActions('commercial_projection', ['getOptimalMixByCustomerChart', 'exportOptimalMix', 'getOptimalMixByProductChart', 'commercialProjectionUpdateOptimalMix', 'getStatisticsOptimalMix']),
    exportData() {
      this.exportOptimalMix({
        projection: this.projection || this.$route.params.id,
        ...this.filters
      })
    },
    async updateMix() {
      this.loadingUpdateMix = true
      const { error } = await this.commercialProjectionUpdateOptimalMix({
        id: this.$route.params.id,
      })
      this.loadingUpdateMix = false

      if (error) {
        this.$toast.error('Ocurrió un error al actualizar el mix óptimo')
        return
      }

      this.$toast.success('Mix óptimo actualizado correctamente')
      this.fetchData()
    },
    async fetchData() {
      const params = {
        ...this.filters,
        projection: this.projection || this.$route.params.id
      }
      this.fetchStatistics()

      const action = this.chartBy === 'customer' ? this.getOptimalMixByCustomerChart : this.getOptimalMixByProductChart

      const { data, error } = await action(params)

      if (!error) {
        const grouped = this.groupTopNSeries(data.datasets, chartsUtils.colors.length - 1)
        this.chartData.datasets = grouped.map((dataset, index) => {
          const color = dataset.label === 'Otros'
            ? '#B9C4CC'
            : chartsUtils.colors[index % chartsUtils.colors.length]
          return {
            label: dataset.label,
            data: dataset.data,
            backgroundColor: color,
            hoverBackgroundColor: color
          }
        })
        this.loaded = true
      }
    },
    // Agrupa las series (clientes/productos) dejando como máximo `maxSeries` y
    // sumando el resto en "Otros", para no exceder la paleta y evitar que dos
    // series distintas reciban el MISMO color en la barra apilada.
    groupTopNSeries(datasets, maxSeries) {
      if (!datasets || datasets.length <= maxSeries) return datasets || []
      const withTotal = datasets.map(d => ({
        d,
        total: (d.data || []).reduce((a, b) => a + (Number(b) || 0), 0)
      }))
      withTotal.sort((a, b) => b.total - a.total)
      const keep = withTotal.slice(0, maxSeries).map(x => x.d)
      const rest = withTotal.slice(maxSeries).map(x => x.d)
      const n = (keep[0] && keep[0].data) ? keep[0].data.length : 12
      const otros = {
        label: 'Otros',
        data: Array.from({ length: n }, (_, i) =>
          rest.reduce((acc, d) => acc + (Number((d.data || [])[i]) || 0), 0))
      }
      return keep.concat(otros)
    },

    async fetchStatistics() {
      const params = {
        ...this.filters,
        projection: this.projection || this.$route.params.id
      }
      const { data, error } = await this.getStatisticsOptimalMix(params)

      if (!error) {
        this.totalProfit = data.total_profit
        this.totalSales = data.total_sales
      }
    },
  },
  watch: {
    filters: {
      handler() {
        this.fetchData()
      },
      deep: true,
    },
    chartBy() {
      this.fetchData()
    }
  },
  created() {
    this.fetchData()
  }
}
</script>

<style scoped lang="scss">
.group-by-select {
  flex-grow: 0;
  width: 300px;
}
</style>
